/**
 * Helper functions for working with survey items
 */

/**
 * Get a short preview of the question text
 * @param {string} content - The full content text
 * @param {number} wordLimit - Maximum number of words to include
 * @returns {string} - Truncated text with ellipsis if needed
 */
export const getContentPreview = (content, wordLimit = 5) => {
  if (!content) return '';
  
  const words = content.split(' ');
  const preview = words.slice(0, wordLimit).join(' ');
  
  return words.length > wordLimit 
    ? `${preview}...` 
    : preview;
};

/**
 * Parse survey items from the raw format
 * Used when importing from text files
 * 
 * @param {string} rawData - The raw survey data
 * @returns {Array} - Parsed survey items
 */
export const parseSurveyItems = (rawData) => {
  const items = [];
  
  // Split by section markers
  const sections = rawData.split('--------------------');
  
  sections.forEach(section => {
    if (!section.trim()) return;
    
    const lines = section.trim().split(/\r?\n/);
    if (lines.length === 0) return;
    
    // Extract the reference ID (comes after ~)
    const firstLine = lines[0].trim();
    if (!firstLine.startsWith('~')) return;
    
    const refId = firstLine.substring(1);
    
    // Default properties
    let title = '';
    let isDelete = false;
    let isNoPrint = false;
    let isNumberedOptions = false;
    const directives = {};
    let questionText = '';
    const options = [];
    
    // Track state for parsing
    let inQuestionText = false;
    let questionTextLines = [];
    
    // Process remaining lines
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      
      if (inQuestionText) {
        if (line.startsWith('*)')) {
          // End of question text, start of options
          inQuestionText = false;
          questionText = questionTextLines.join('\n');
          
          // Parse this option
          parseOption(line, options);
        } else {
          questionTextLines.push(line);
        }
      } else if (line.startsWith('.title')) {
        title = line.substring('.title'.length).trim();
      } else if (line.startsWith('.template')) {
        directives['template'] = line.substring('.template'.length).trim();
      } else if (line.startsWith('.delete')) {
        isDelete = true;
      } else if (line.startsWith('.noprint')) {
        isNoPrint = true;
      } else if (line.startsWith('.num')) {
        isNumberedOptions = true;
      } else if (line.startsWith('.qt')) {
        inQuestionText = true;
        questionTextLines = [];
      } else if (line.startsWith('.')) {
        // This is another directive
        const parts = line.substring(1).split(/\s+/, 2);
        if (parts.length > 0) {
          const key = parts[0];
          const value = parts.length > 1 ? parts[1] : '';
          directives[key] = value;
        }
      } else if (line.startsWith('*)')) {
        parseOption(line, options);
      }
    }
    
    // If we're still in question text mode, save it
    if (inQuestionText && questionTextLines.length > 0) {
      questionText = questionTextLines.join('\n');
    }
    
    // Determine type based on content and options
    let type = 'question';
    if (options.length === 0) {
      type = 'informational';
    } else if (options.length === 1 && options[0].isOpen) {
      type = 'open-ended';
    } else if (questionText.toLowerCase().includes('rate')) {
      type = 'rating';
    }
    
    items.push({
      refId,
      title,
      type,
      visible: !isDelete && !isNoPrint,
      isNumberedOptions,
      directives,
      content: questionText,
      options,
      delete: isDelete,
      noPrint: isNoPrint
    });
  });
  
  return items;
};

/**
 * Parse an option from a line of text
 * 
 * @param {string} line - Line of text containing the option
 * @param {Array} options - Array to add the option to
 */
const parseOption = (line, options) => {
  if (!line.startsWith('*)')) return;
  
  // Remove the option marker
  const optionText = line.substring('*)'.length).trim();
  
  const option = {
    text: optionText,
    isVisible: true,
    isTerminate: false,
    isRotate: false,
    isOpen: false
  };
  
  if (optionText.startsWith('-')) {
    option.isVisible = false;
  }
  
  // Check for terminate
  if (optionText.endsWith('(TERMINATE)')) {
    option.isTerminate = true;
    option.text = optionText.replace('(TERMINATE)', '').trim();
  }
  
  // Check for open-ended
  if (optionText === '??' || optionText === 'XX') {
    option.isOpen = true;
  }
  
  options.push(option);
};

/**
 * Format survey items for export
 * 
 * @param {Array} items - Survey items to export
 * @returns {string} - Formatted string for export
 */
export const formatSurveyItemsForExport = (items) => {
  let output = '';
  
  items.forEach(item => {
    output += '--------------------\n';
    output += `~${item.refId}\n`;
    
    if (item.title) {
      output += `.title ${item.title}\n`;
    }
    
    if (item.delete) {
      output += '.delete\n';
    }
    
    if (item.noPrint) {
      output += '.noprint\n';
    }
    
    if (item.directives && item.directives.template) {
      output += `.template ${item.directives.template}\n`;
    }
    
    if (item.isNumberedOptions) {
      output += '.num\n';
    }
    
    // Add directives
    if (item.directives) {
      Object.entries(item.directives).forEach(([key, value]) => {
        // Skip template since we've already handled it
        if (key !== 'template') {
          output += `.${key} ${value}\n`;
        }
      });
    }
    
    // Add question text
    if (item.content) {
      output += '.qt\n';
      output += item.content + '\n';
    }
    
    // Add options
    if (item.options && item.options.length > 0) {
      item.options.forEach(option => {
        const terminateText = option.isTerminate ? ' (TERMINATE)' : '';
        const visibilityPrefix = option.isVisible ? '' : '-';
        output += `*) ${visibilityPrefix}${option.text}${terminateText}\n`;
      });
    }
    
    output += '--------------------\n';
  });
  
  return output;
};